import { useState, useEffect } from 'react';
import { CreditCard, Check, AlertCircle, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { stripePlans } from '../config/stripePlans';

interface SubscriptionRequiredProps {
  trialEndsAt: string | null;
  onRefresh: () => Promise<void>;
}

export function SubscriptionRequired({ trialEndsAt, onRefresh }: SubscriptionRequiredProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    console.log('SubscriptionRequired component mounted, checking subscription status...');
    const checkOnMount = async () => {
      await onRefresh();
    };
    checkOnMount();
  }, []);

  async function handleLogout() {
    await supabase.auth.signOut();
  }

  async function handleRefresh() {
    console.log('Manual refresh button clicked');
    setRefreshing(true);
    try {
      await onRefresh();
      console.log('Refresh completed');
    } catch (error) {
      console.error('Error during refresh:', error);
    } finally {
      setRefreshing(false);
    }
  }

  async function handleSubscribe(planType: 'essentiel' | 'metier' | 'pilotage' | 'daf') {
    if (!user) return;

    setLoading(planType);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      const plan = stripePlans[planType];
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          priceId: plan.priceId,
          planType: planType,
          userId: user.id,
          userEmail: user.email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Checkout session error:', errorData);
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('Checkout session response:', data);

      if (!data.url) {
        throw new Error('No checkout URL received from server');
      }

      const stripeWindow = window.open(data.url, '_blank');
      if (!stripeWindow) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(null);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-7xl w-full">
        <div className="mb-8">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
        </div>

        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-orange-600" size={32} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Votre période d'essai est terminée
          </h1>
          {trialEndsAt && (
            <p className="text-lg text-gray-600">
              Votre essai gratuit a pris fin le {new Date(trialEndsAt).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          )}
          <p className="text-lg text-gray-600 mt-2">
            Choisissez un abonnement pour continuer à utiliser TrezoOne
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 hover:shadow-lg transition-all">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{stripePlans.essentiel.name}</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">{stripePlans.essentiel.monthlyPrice}€</span>
                <span className="text-gray-600 text-sm">/mois</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6 min-h-[180px]">
              {stripePlans.essentiel.features.slice(0, 5).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-gray-700">
                  <Check className="text-green-600 flex-shrink-0 mt-0.5" size={14} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe('essentiel')}
              disabled={loading !== null}
              className="w-full bg-gray-900 text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading === 'essentiel' ? 'Redirection...' : 'Choisir'}
            </button>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl border-2 border-blue-800 p-6 hover:shadow-lg transition-all relative">
            <div className="absolute top-0 right-0 bg-yellow-400 text-gray-900 px-3 py-1 rounded-bl-lg rounded-tr-2xl font-semibold text-xs">
              Populaire
            </div>

            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-white mb-2">{stripePlans.metier.name}</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-3xl font-bold text-white">{stripePlans.metier.monthlyPrice}€</span>
                <span className="text-blue-100 text-sm">/mois</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6 min-h-[180px]">
              {stripePlans.metier.features.slice(0, 5).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-white">
                  <Check className="text-yellow-300 flex-shrink-0 mt-0.5" size={14} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe('metier')}
              disabled={loading !== null}
              className="w-full bg-white text-blue-600 px-4 py-2.5 rounded-lg hover:bg-blue-50 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading === 'metier' ? 'Redirection...' : 'Choisir'}
            </button>
          </div>

          <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 hover:shadow-lg transition-all">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{stripePlans.pilotage.name}</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">{stripePlans.pilotage.monthlyPrice}€</span>
                <span className="text-gray-600 text-sm">/mois</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6 min-h-[180px]">
              {stripePlans.pilotage.features.slice(0, 5).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-gray-700">
                  <Check className="text-green-600 flex-shrink-0 mt-0.5" size={14} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe('pilotage')}
              disabled={loading !== null}
              className="w-full bg-gray-900 text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading === 'pilotage' ? 'Redirection...' : 'Choisir'}
            </button>
          </div>

          <div className="bg-white rounded-2xl border-2 border-amber-300 p-6 hover:shadow-lg transition-all">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{stripePlans.daf.name}</h3>
              <div className="flex items-baseline justify-center gap-2 mb-2">
                <span className="text-3xl font-bold text-gray-900">{stripePlans.daf.monthlyPrice}€</span>
                <span className="text-gray-600 text-sm">/mois</span>
              </div>
            </div>

            <ul className="space-y-2 mb-6 min-h-[180px]">
              {stripePlans.daf.features.slice(0, 5).map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-xs text-gray-700">
                  <Check className="text-green-600 flex-shrink-0 mt-0.5" size={14} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSubscribe('daf')}
              disabled={loading !== null}
              className="w-full bg-amber-600 text-white px-4 py-2.5 rounded-lg hover:bg-amber-700 transition-colors font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading === 'daf' ? 'Redirection...' : 'Choisir'}
            </button>
          </div>
        </div>

        <div className="text-center">
          <div className="mb-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm underline disabled:opacity-50"
            >
              {refreshing ? 'Vérification...' : 'J\'ai déjà souscrit'}
            </button>
          </div>

          <p className="text-sm text-gray-500">
            Paiement sécurisé par Stripe • Résiliable à tout moment
          </p>
        </div>

        <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-100">
          <h3 className="font-semibold text-gray-900 mb-3">Ce qui est inclus :</h3>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-gray-700">
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Import automatique des opérations</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Trésorerie en temps réel</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Budget prévisionnel</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Scénarios de simulation</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Plan de trésorerie</span>
            </div>
            <div className="flex items-start gap-2">
              <Check className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
              <span>Support client réactif</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
